import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const getFullPath = (location) =>
  `${location.pathname}${location.search}${location.hash}`;

export default function useAuthModal() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState('search');
  const [redirectTo, setRedirectTo] = useState('');

  const openAuth = useCallback(
    (action = 'search', nextRedirectTo) => {
      setPendingAction(action);
      setRedirectTo(nextRedirectTo || getFullPath(location));
      setIsOpen(true);
    },
    [location]
  );

  // Backward-compatible aliases for older call sites.
  const open = useCallback(
    (action = 'search', nextRedirectTo) => openAuth(action, nextRedirectTo),
    [openAuth]
  );

  const closeAuth = useCallback(() => {
    setIsOpen(false);
  }, []);

  const close = useCallback(() => closeAuth(), [closeAuth]);

  useEffect(() => {
    if (!location.state?.openAuth) return;
    const nextAction = location.state?.action || 'search';
    const nextRedirectTo = location.state?.redirectTo || getFullPath(location);
    setPendingAction(nextAction);
    setRedirectTo(nextRedirectTo);
    setIsOpen(true);
    navigate(getFullPath(location), { replace: true, state: null });
  }, [location, navigate]);

  return useMemo(
    () => ({
      isOpen,
      pendingAction,
      redirectTo,
      openAuth,
      closeAuth,
      open,
      close,
    }),
    [isOpen, pendingAction, redirectTo, openAuth, closeAuth, open, close]
  );
}
